import axios from 'axios';
import { SearchData, SelectGrouped } from 'ts/transfer/interfaces/Transfer';
import { SearchTransferModel } from 'ts/transfer/classes/SearchTransferModel';
import {
  BookingModel,
  TransferBookingResponse,
  TransferResultsResponse,
} from 'ts/transfer/interfaces/TransferResultsResponse';
import { OwenerTransferModel } from 'ts/transfer/classes/OwenerTransferModel';
import i18n from 'utils/transfer';
import { Screenshot } from 'utils/utils';
import { SubmitScreenshotsParams } from 'ts/shared/interfaces/Generic';

export interface BookingParams {
  searchToken: string;
  ukeyForward: string;
  ukeyReturn: string;
  owner?: OwenerTransferModel;
}

export const getSearchData = async (): Promise<SearchData> => {
  try {
    const url = 'Transfers/GetSearchFilters';
    const lang = localStorage.getItem('language') === 'en' ? 'En' : 'He';
    const data = (await axios.get(url, { params: { lang } })).data;

    return {
      people: data.people
        .filter(
          (people: any, index: number, arr: any) =>
            arr.findIndex((t: any) => t.text === people.text) === index
        )
        .map((people: any) => ({
          value: +people.value,
          label: people.text,
        })),
    };
  } catch (err) {
    return {
      people: [],
    };
  }
};

export const getAutoComplete = async (
  term: string,
  ctrl: AbortController
): Promise<SelectGrouped[]> => {
  try {
    const url = 'Transfers/MultiAutocomplete';
    const config = { params: { term }, signal: ctrl.signal };
    const { data } = await axios.get(url, config);

    const airports = data.airports
      .filter((airport: any) => airport.code !== airport.airCityLang?.code)
      .map((airport: any) => ({
        value: airport.code,
        label: `${airport.code} - ${airport.descWWW}`,
        data: {
          lat: airport.gisX,
          lng: airport.gisY,
          countryCode: airport.countryCode,
        },
      }));

    const addresses = data.addresses.map((address: any) => ({
      value: address.place_id,
      label: address.description,
    }));

    return [
      { label: i18n.t('transfer.airports'), options: airports },
      { label: i18n.t('transfer.addresses'), options: addresses },
    ];
  } catch (err) {
    return [];
  }
};

export const search = async (
  data: SearchTransferModel
): Promise<TransferResultsResponse | undefined> => {
  try {
    const url = 'Transfers/Search';

    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};

export const booking = async (
  data: BookingParams
): Promise<TransferBookingResponse | undefined> => {
  try {
    const url = 'Transfers/Booking';

    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};

export const pay = async (data: BookingParams): Promise<TransferBookingResponse | undefined> => {
  try {
    const url = 'Transfers/BookingPayment';

    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};

export const bookComplete = async (
  data: BookingModel
): Promise<
  | {
      data: BookingModel;
      bookingSuccess: boolean;
      errorMessage: string;
      hasErrors: boolean;
      sessionExpired: boolean;
    }
  | undefined
> => {
  try {
    const screenshotsJson = sessionStorage.getItem('screenshotsTransfer');
    let screenshots: string[] = [];
    if (screenshotsJson) {
      const screenshotsArr: Screenshot[] = JSON.parse(screenshotsJson);
      screenshots = screenshotsArr.map((screenShots) => screenShots.data);
    }
    const language = 1;
    const url = 'Transfers/BookComplete';

    return (await axios.post(url, { ...data, language, screenshots })).data;
  } catch (err) {
    return undefined;
  }
};

export const checkPaymentStatus = async (transactionId: string): Promise<any | undefined> => {
  try {
    const url = 'Transfers/CheckPaymentStatus';

    return (await axios.post(url, { transactionId })).data;
  } catch (err) {
    return undefined;
  }
};

export const submitScreenshots = async (params: SubmitScreenshotsParams): Promise<any> => {
  try {
    const url = 'Transfers/SubmitScreenshots';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

interface UpdatePaymentBlockParams {
  sourcePrice: number;
  packageCurrency: string;
  addPrice: number;
  searchToken: string;
  lang: string;
}

export const updatePaymentBlock = async (params: UpdatePaymentBlockParams) => {
  try {
    const url = 'Utils/UpdatePaymentBlock';
    return (await axios.post(url, undefined, { params })).data;
  } catch (err) {
    return undefined;
  }
};
